import { Link, Navigate } from 'react-router-dom'
import { useEffect, useMemo, useRef, useState } from 'react'
import { RotateCcw, Mail, X } from 'lucide-react'
import { ScreenTransition } from '@/components/ScreenTransition'
import { ProductCard } from '@/components/ProductCard'
import { Carousel } from '@/components/Carousel'
import { EmailCapture } from '@/components/EmailCapture'
import { PANTS, BIBS } from '@/data/products'
import { RESULT_QUOTES } from '@/data/quotes'
import { pickBullets } from '@/data/whyThisMatch'
import { BEATRICE_ALT } from '@/data/beatriceAlternative'
import { buildValidationLine } from '@/lib/validation'
import { buildProductUrl } from '@/lib/urls'
import { submitQuiz, generateSubmissionId } from '@/lib/submit'
import { scoreQuiz } from '@/lib/scoring'
import { useQuiz } from '@/state/QuizContext'
import { pingScreen } from '@/lib/ping'

/**
 * Screen 5 - Result. Card layout:
 *
 *   1. Validation line (mirrors pain points back)
 *   2. Why this is your cut - 3 bullets
 *   3. Product grid (pant + bib, equal weight)
 *   4. Customer quote (placeholder)
 *   5. Live chat CTA (stubbed as mailto until Phase 7 wires real widget)
 *   6. Email capture
 *   7. Start over
 *
 * Submission pipeline (Phase 4):
 *   - On mount: anonymous completion -> POST /.netlify/functions/submit
 *   - On email capture: same submission_id -> upsert with email
 *
 * All hooks run unconditionally - rules-of-hooks requires stable order.
 * Safe values fall through when the quiz is incomplete, and the render
 * guard below redirects before any incomplete data is used.
 */
export function ResultScreen() {
  const { isComplete, asAnswers, reset } = useQuiz()

  // ── Hooks (always called, in stable order) ─────────────────────────────
  const answers = asAnswers()
  const result = useMemo(
    () => (answers ? scoreQuiz(answers) : null),
    [answers]
  )

  const bullets = useMemo(() => {
    if (!answers || !result) return []
    return pickBullets(answers.painPoints, result.recommended_cut)
  }, [answers, result])

  // Stable submission ID - shared across the anonymous-on-mount fire and
  // the email-capture fire. Server upserts on the ID so the record is
  // updated in place rather than duplicated.
  const submissionIdRef = useRef<string>(generateSubmissionId())

  // Fire the anonymous completion exactly once on mount. Errors are
  // swallowed so a flaky network doesn't block the result UI. Guarded with
  // a ref to stay idempotent under StrictMode's dev double-invoke.
  const firedAnonRef = useRef(false)
  const [alternativeOpen, setAlternativeOpen] = useState(false)

  useEffect(() => { pingScreen('result') }, [])

  // Close the "need it sooner?" modal on Esc and lock body scroll while open.
  useEffect(() => {
    if (!alternativeOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setAlternativeOpen(false)
    }
    window.addEventListener('keydown', onKey)
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      window.removeEventListener('keydown', onKey)
      document.body.style.overflow = prevOverflow
    }
  }, [alternativeOpen])

  useEffect(() => {
    if (!answers || !result) return
    if (firedAnonRef.current) return
    firedAnonRef.current = true
    submitQuiz({
      submission_id: submissionIdRef.current,
      email:         null,
      answers,
      client_result: result,
    }).catch((err) => {
      // eslint-disable-next-line no-console
      console.warn('[fitquiz] anonymous completion submit failed', err)
    })
  }, [answers, result])

  // ── Guard: redirect incomplete visitors back to the start ──────────────
  if (!isComplete || !answers || !result) return <Navigate to="/" replace />

  // ── Derived values (safe - we've confirmed answers + result above) ─────
  const pant = PANTS[result.recommended_cut]
  const bib  = BIBS[result.bib_cut]
  const quote = RESULT_QUOTES[result.recommended_cut]
  const validationLine = buildValidationLine(
    answers.painPoints,
    result.recommended_cut,
    answers.bodyShape,
    answers.heightBand,
  )

  // The Beatrice-tall case keeps the Beatrice cut but drops the length to
  // regular. The validation lead-in already explains the trade-off, so don't
  // double up with the generic "we adjusted your match" note. We also use
  // this flag to render a small "prefer length over fit?" link beneath the
  // primary card pointing at the Mila tall as an alternative.
  const isBeatriceTallSwap =
    answers.heightBand === 'tall' && result.recommended_cut === 'beatrice'
  const showFallbackNote = result.fallback_triggered && !isBeatriceTallSwap

  const sharedForUrl = {
    recommended_cut:    result.recommended_cut,
    recommended_length: result.recommended_length,
    bodyShape:          answers.bodyShape,
  }
  // Each length is its own Shopify product, so the handle is keyed by
  // result.recommended_length (which has already had the Beatrice->Mila
  // tall fallback applied).
  const pantHandle = pant.handles[result.recommended_length]
  const bibHandle  = bib.handles[result.bib_length]
  const pantUrl = buildProductUrl(pantHandle, 'quiz_direct', sharedForUrl)
  const bibUrl  = buildProductUrl(bibHandle,  'quiz_direct', {
    ...sharedForUrl,
    recommended_length: result.bib_length,
  })
  const pantLengthLabel = capitalise(result.recommended_length)
  const bibLengthLabel  = capitalise(result.bib_length)

  // Pre-launch handling for Beatrice. When the recommended pant isn't yet
  // buyable, we keep the Beatrice card as the primary match (it's still the
  // right cut for her body), but:
  //   1. Show an "Arriving June 2026" chip over the image.
  //   2. Swap the CTA to "Learn more" pointing at the PDP where she can sign
  //      up to be notified at launch.
  //   3. Render an alternative Mila pant beneath the grid with three
  //      differentiators so customers who need something sooner have a real
  //      path to purchase today.
  // The tall-fallback case (apple/rectangle + tall) is already redirected
  // from Beatrice to Mila by the scoring module, so it never hits this
  // branch - nothing to special-case there.
  const showBeatricePreorder = pant.comingSoon != null
  const milaAltPant      = PANTS.mila
  // Use the customer's actual height (answers.heightBand) here, not the
  // pant recommendation - for a Beatrice-tall match the pant has been
  // dropped to regular but the customer is still tall, and Mila ships in
  // tall, so the alternative should reflect that.
  const milaAltLength    = answers.heightBand
  const milaAltHandle    = milaAltPant.handles[milaAltLength]
  const milaAltUrl       = buildProductUrl(milaAltHandle, 'quiz_direct', {
    ...sharedForUrl,
    recommended_cut:    'mila',
    recommended_length: milaAltLength,
  })
  const milaAltLengthLabel = capitalise(milaAltLength)

  // For the Beatrice-tall swap: the customer asked for tall but we're showing
  // them the regular Beatrice. Build a link to the actual Mila tall (not the
  // regular-length Mila) as a "prefer length over fit?" alternative.
  const milaTallHandle = PANTS.mila.handles.tall
  const milaTallUrl    = buildProductUrl(milaTallHandle, 'quiz_direct', {
    recommended_cut:    'mila',
    recommended_length: 'tall',
    bodyShape:          answers.bodyShape,
  })

  // Phase 4 stub - the chat button opens a pre-filled mailto until Maria
  // chooses a live-chat widget (decision parked; see separate research).
  const chatHref = buildChatHandoffHref(result.recommended_cut, answers)

  async function handleEmailSubmit(email: string) {
    if (!answers || !result) return
    await submitQuiz({
      submission_id: submissionIdRef.current,
      email,
      answers,
      client_result: result,
    })
  }

  return (
    <ScreenTransition className="fq-result">
      <div className="fq-result-inner">
        {/*
          Desktop: 2-col grid. Left column = Header + Products. Right column =
          Why card (top, aligned with "Your match") + sidebar card (quote,
          save, chat, footer). Mobile stacks in DOM order.
        */}
        <div className="fq-result-body">
          <div className="fq-result-main">
            <header className="fq-result-header">
              <p className="fq-eyebrow">Made for you</p>
              <h2 className="fq-h2">
                Your match: <span className="fq-accent">our {capitalise(result.recommended_cut)} pant.</span>
              </h2>
              <p className="fq-validation">{validationLine}</p>
              {showFallbackNote && (
                <p className="fq-fallback-note">
                  We adjusted your match to make sure every piece is available in your length.
                </p>
              )}
            </header>

            <Carousel>
            <section className="fq-product-grid" aria-label="Recommended products">
              <ProductCard
                product={pant}
                handle={pantHandle}
                ratingKey={result.recommended_cut}
                ctaUrl={pantUrl}
                ctaLabel={showBeatricePreorder ? 'Learn more' : undefined}
                lengthLabel={pantLengthLabel}
                comingSoonLabel={pant.comingSoon?.label}
                secondaryChip={
                  showBeatricePreorder
                    ? { label: 'Need it sooner?', onClick: () => setAlternativeOpen(true) }
                    : undefined
                }
                footnote={
                  isBeatriceTallSwap ? (
                    <>
                      We don't offer Beatrice in a tall length yet, so we'd recommend the regular - it'll sit a touch above your boot but the cut still wraps your shape the way you need.{' '}
                      <a href={milaTallUrl} target="_top" rel="noopener" className="fq-length-alt-link">
                        Prefer length over fit? See our Mila tall →
                      </a>
                    </>
                  ) : undefined
                }
              />
              <ProductCard
                product={bib}
                handle={bibHandle}
                ratingKey={result.bib_cut + '-bib'}
                ctaUrl={bibUrl}
                lengthLabel={bibLengthLabel}
                footnote={
                  result.bib_available
                    ? undefined
                    : 'Our Mila bib is the best match for your shape in a bib style - same generous mid-section room, same adjustability.'
                }
              />
            </section>
            </Carousel>

          </div>

          <aside className="fq-result-sidebar">
            <section className="fq-why fq-sidebar-card" aria-labelledby="fq-why-title">
              <h3 id="fq-why-title" className="fq-h3">Why this is your cut</h3>
              <ul className="fq-why-list">
                {bullets.map((b, i) => (
                  <li key={i}>{b}</li>
                ))}
              </ul>
              <figure className="fq-quote fq-quote--result">
                <blockquote>"{quote.text}" <span className="fq-quote-attr">- {quote.attribution}</span></blockquote>
              </figure>
            </section>

            <div className="fq-sidebar-card">
              <section className="fq-email-section" aria-labelledby="fq-email-title">
                <h3 id="fq-email-title" className="fq-h3">Save your results</h3>
                <p className="fq-lede">
                  Pop in your email and we'll send a copy of your match summary to your inbox.
                </p>
                <EmailCapture onSubmit={handleEmailSubmit} />
              </section>

              <section className="fq-secondary-ctas">
                <a className="fq-cta fq-cta--chat" href={chatHref}>
                  <Mail size={20} aria-hidden="true" />
                  <span className="fq-cta-chat-label">
                    <span className="fq-cta-chat-line1">Still unsure?</span>
                    <span className="fq-cta-chat-line2">Chat with our team.</span>
                  </span>
                </a>
              </section>

              <footer className="fq-result-footer">
                <Link to="/" onClick={reset} className="fq-start-over">
                  <RotateCcw size={14} aria-hidden="true" />
                  Start over
                </Link>
              </footer>
            </div>
          </aside>
        </div>
      </div>

      {showBeatricePreorder && alternativeOpen && (
        <div
          className="fq-modal-backdrop"
          role="dialog"
          aria-modal="true"
          aria-labelledby="fq-alternative-title"
          onClick={() => setAlternativeOpen(false)}
        >
          <div
            className="fq-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="fq-modal-close"
              aria-label="Close"
              onClick={() => setAlternativeOpen(false)}
            >
              <X size={20} aria-hidden="true" />
            </button>
            <section className="fq-alternative" aria-labelledby="fq-alternative-title">
              <div className="fq-alternative-copy">
                <h3 id="fq-alternative-title" className="fq-h3">
                  <span className="fq-accent">Need it sooner?</span><br />Your next best match is our Mila.
                </h3>
                <p className="fq-lede">{BEATRICE_ALT.intro}</p>
              </div>
              <div className="fq-alternative-card">
                <ProductCard
                  product={milaAltPant}
                  handle={milaAltHandle}
                  ratingKey="mila"
                  ctaUrl={milaAltUrl}
                  lengthLabel={milaAltLengthLabel}
                />
              </div>
              <ul className="fq-alternative-bullets">
                {BEATRICE_ALT.bullets.map((b, i) => (
                  <li key={i}>{b}</li>
                ))}
              </ul>
            </section>
          </div>
        </div>
      )}
    </ScreenTransition>
  )
}

function capitalise(s: string): string {
  return s.length === 0 ? s : s[0].toUpperCase() + s.slice(1)
}

/**
 * Placeholder chat handoff - writes a prefilled mailto so the link works
 * during review. Will be swapped for the real live-chat widget open call
 * once Maria picks a tool (decision parked; see separate research prompt).
 */
function buildChatHandoffHref(
  cut: string,
  answers: { painPoints: readonly string[]; bodyShape: string; heightBand: string }
): string {
  const body = [
    "Hi NP team - I just took the fit quiz and I'd love a hand deciding.",
    '',
    `Recommended cut: ${cut}`,
    `Body shape: ${answers.bodyShape}`,
    `Height band: ${answers.heightBand}`,
    `Pain points: ${answers.painPoints.join(', ')}`,
  ].join('\n')
  // Use encodeURIComponent (not URLSearchParams) so spaces become %20 and
  // newlines become %0A. Gmail and some other clients render URLSearchParams'
  // `+` and unencoded newlines literally in the mailto body.
  const subject = encodeURIComponent('Fit quiz - help me decide')
  const bodyEnc = encodeURIComponent(body)
  return `mailto:hello@nobodysprincess.com.au?subject=${subject}&body=${bodyEnc}`
}
